import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { useSearchedOptions } from '@/hooks/useSearchedOptions'
import { getApi } from '@/stores/api'
import { GOOGLE_FINANCIAL_STATEMENT_TYPES, GoogleFinancialStatementType } from '@repo/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const googleFinancialStatementsSearchSchema = z.object({
  type: z.enum(GOOGLE_FINANCIAL_STATEMENT_TYPES).optional(),
  keyword: z.string().optional(),
  legalName: z.array(z.string()).optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export type GoogleFinancialStatementsSearch = z.infer<typeof googleFinancialStatementsSearchSchema>

type GoogleFinancialStatementsQuery = GoogleFinancialStatementsSearch & {
  type: GoogleFinancialStatementType
}

async function getGoogleFinancialStatements(
  queries: GoogleFinancialStatementsQuery,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['google-financial-statements'].$get(
    { query: { ...stringifyValues(queries), type: queries.type } },
    { init: { signal } },
  )

  return await res.json()
}

export type GoogleFinancialStatementRow = Awaited<
  ReturnType<typeof getGoogleFinancialStatements>
>['results'][number]

export function useGoogleFinancialStatements(queries: GoogleFinancialStatementsQuery) {
  return useQuery({
    queryKey: ['googleFinancialStatements', queries],
    queryFn: ({ signal }) => getGoogleFinancialStatements(queries, signal),
  })
}

async function getGoogleFinancialStatementYears(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['google-financial-statements'].years.$get({}, { init: { signal } })

  const { years } = await res.json()
  return years
}

export function useGoogleFinancialStatementYears() {
  return useQuery({
    queryKey: ['googleFinancialStatementYears'],
    queryFn: ({ signal }) => getGoogleFinancialStatementYears(signal),
  })
}

async function getGoogleFinancialStatementsLastImport(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['google-financial-statements']['last-import'].$get(
    {},
    { init: { signal } },
  )

  const { lastImport } = await res.json()
  return lastImport
}

export function useGoogleFinancialStatementsLastImport() {
  return useQuery({
    queryKey: ['googleFinancialStatementsLastImport'],
    queryFn: ({ signal }) => getGoogleFinancialStatementsLastImport(signal),
  })
}

async function getGoogleFinancialStatementImports(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.credit['google-financial-statements'].imports.$get({}, { init: { signal } })

  const { imports } = await res.json()
  return imports
}

export type GoogleFinancialStatementImport = Awaited<
  ReturnType<typeof getGoogleFinancialStatementImports>
>[number]

export function useGoogleFinancialStatementImports() {
  return useQuery({
    queryKey: ['googleFinancialStatementImports'],
    queryFn: ({ signal }) => getGoogleFinancialStatementImports(signal),
  })
}

export function useDeleteGoogleFinancialStatementImport() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (id: number) => {
      const api = getApi()
      const res = await api.credit['google-financial-statements'].imports[':id'].$delete({
        param: { id: String(id) },
      })
      return await res.json()
    },
    onSuccess: () => {
      // The deleted import's rows are gone; refresh every google-financial-statements view.
      for (const key of [
        'googleFinancialStatementImports',
        'googleFinancialStatements',
        'googleFinancialStatementYears',
        'googleFinancialStatementLegalNames',
        'googleFinancialStatementsLastImport',
        'googleFinancialStatementDetail',
        'googleFinancialStatementErpDetail',
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(t('googleFinancialStatements.importHistory.deleteSuccess'))
    },
    onError: () => {
      toast.error(t('googleFinancialStatements.importHistory.deleteError'))
    },
  })
}

async function getGoogleFinancialStatementLegalNames(
  year: number | undefined,
  search: string,
  offset: number,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['google-financial-statements']['legal-names'].$get(
    { query: stringifyValues({ year, search, offset }) },
    { init: { signal } },
  )

  const { legalNames, nextOffset } = await res.json()
  return { options: legalNames.map((name) => ({ value: name, label: name })), nextOffset }
}

/** Legal name options for the list's filter, searched as the user types; `selected` always offered. */
export function useGoogleFinancialStatementLegalNameOptions(selected: string[], year?: number) {
  return useSearchedOptions({
    queryKey: 'googleFinancialStatementLegalNames',
    scope: year,
    selected,
    fetchMatches: (search, offset, signal) =>
      getGoogleFinancialStatementLegalNames(year, search, offset, signal),
  })
}

type GoogleFinancialStatementDetailParams = {
  sapErp: string
  type: GoogleFinancialStatementType
}

async function getGoogleFinancialStatementDetail(
  params: GoogleFinancialStatementDetailParams,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['google-financial-statements'].detail.$get(
    { query: { sapErp: params.sapErp, type: params.type } },
    { init: { signal } },
  )

  const { history } = await res.json()
  return history
}

export type GoogleFinancialStatementHistoryItem = Awaited<
  ReturnType<typeof getGoogleFinancialStatementDetail>
>[number]

export function useGoogleFinancialStatementDetail(
  params: GoogleFinancialStatementDetailParams | null,
) {
  return useQuery({
    queryKey: ['googleFinancialStatementDetail', params],
    queryFn: ({ signal }) => getGoogleFinancialStatementDetail(params!, signal),
    enabled: params !== null,
  })
}

async function getGoogleFinancialStatementErpDetail(
  sapErp: string,
  asOf?: Date,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.credit['google-financial-statements']['erp-detail'].$get(
    { query: { sapErp, ...(asOf ? { asOf: asOf.toISOString() } : {}) } },
    { init: { signal } },
  )

  return await res.json()
}

export type GoogleFinancialStatementErpDetail = Awaited<
  ReturnType<typeof getGoogleFinancialStatementErpDetail>
>
export type GoogleFinancialStatementErpCell = GoogleFinancialStatementErpDetail['cells'][number]
export type GoogleFinancialStatementErpMetadata = GoogleFinancialStatementErpDetail['metadata']

/** Query options so the route loader can prefetch the detail (for the breadcrumb) and the page reuses it. */
export function googleFinancialStatementErpDetailQueryOptions(sapErp: string, asOf?: Date) {
  return {
    // `asOf` in the key: the ratios grid a resolved CLR shows is a different result from the live
    // one and must not share its cache entry.
    queryKey: ['googleFinancialStatementErpDetail', sapErp, asOf?.toISOString() ?? null],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getGoogleFinancialStatementErpDetail(sapErp, asOf, signal),
  }
}

export function useGoogleFinancialStatementErpDetail(sapErp: string, asOf?: Date) {
  return useQuery(googleFinancialStatementErpDetailQueryOptions(sapErp, asOf))
}

/**
 * Writes the note on one ratio of one year. The backend appends a row rather than updating in
 * place, so the ERP grid, the per-year history and the list page all move at once — invalidate all
 * three rather than patching the cache.
 */
export function useUpdateGoogleFinancialStatementComment(sapErp: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      type: GoogleFinancialStatementType
      year: number
      comment: string | null
    }) => {
      const api = getApi()
      const res = await api.credit['google-financial-statements'][':sapErp'].comment.$patch({
        param: { sapErp },
        json: input,
      })
      if (!res.ok) throw new Error('Failed to update the financial statement comment')
      return await res.json()
    },
    onSuccess: () => {
      for (const key of [
        'googleFinancialStatementErpDetail',
        'googleFinancialStatementDetail',
        'googleFinancialStatements',
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(t('googleFinancialStatements.detail.commentSaveSuccess'))
    },
    onError: () => {
      toast.error(t('googleFinancialStatements.detail.commentSaveError'))
    },
  })
}

type GoogleFinancialStatementsExportFilters = {
  type: GoogleFinancialStatementType
  keyword?: string
  legalName?: string[]
}

async function getGoogleFinancialStatementsCsv(
  filters: GoogleFinancialStatementsExportFilters,
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(filters)
  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  return fetchCsvExport(`/credit/google-financial-statements/export?${queryParams}`, signal)
}

export function googleFinancialStatementsExportQueryOptions(
  filters: GoogleFinancialStatementsExportFilters,
) {
  return {
    queryKey: ['googleFinancialStatementsExport', filters],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getGoogleFinancialStatementsCsv(filters, signal),
  }
}
